"use client"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Star, Phone, Globe, ChevronDown, ChevronUp, MapPin, Clock } from "lucide-react"
import { useSubcontractors } from "./useSubcontractors"
import { useFetchSubcontractorDetails } from "./useFetchSubcontractorDetails"

function Subcontractors({ naics, description, streetAddress, city, state, zip, country }) {
  const [subcontractorRequest] = useState({
    streetAddress,
    city,
    state,
    zip,
    country,
    description,
    naics,
  })
  const { data, isLoading, error } = useSubcontractors(subcontractorRequest)
  if (isLoading) {
    return (
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} className="overflow-hidden">
            <CardHeader className="pb-0">
              <Skeleton className="h-6 w-2/3" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full mt-2" />
              <Skeleton className="h-4 w-3/4 mt-2" />
              <Skeleton className="h-10 w-full mt-4" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error) {
    return <div className="mt-8 text-red-600 text-center">Error fetching subcontractors: {error.message}</div>
  }

  if (!data || data.length === 0) {
    return <div className="mt-8 text-gray-600 text-center">No subcontractors found.</div>
  }

  return (
    <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {data.map((subcontractor, index) => (
        <SubcontractorCard key={index} subcontractor={subcontractor} />
      ))}
    </div>
  )
}

function SubcontractorCard({ subcontractor }) {
  const [showDetails, setShowDetails] = useState(false)
  const {
    data: details,
    isLoading: detailsLoading,
    error: detailsError,
  } = useFetchSubcontractorDetails(showDetails ? subcontractor.place_id : null)
  const toggleDetails = () => {
    setShowDetails((prev) => !prev)
  }

  return (
    <Card className="overflow-hidden transition-all duration-300 hover:shadow-lg">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl font-bold truncate">{subcontractor.name || "Subcontractor Name"}</CardTitle>
        <CardDescription className="flex items-center justify-between mt-1">
          <div className="flex items-center">
            <Star className="w-4 h-4 text-yellow-400 mr-1 fill-current" />
            <span className="font-semibold">{subcontractor.rating || "N/A"}</span>
            <span className="text-sm text-gray-500 ml-1">({subcontractor.user_ratings_total || "0"} reviews)</span>
          </div>
          {subcontractor.business_status === "OPERATIONAL" && (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              Open
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <div className="flex items-center text-sm text-gray-600">
          <MapPin className="w-4 h-4 mr-1" />
          {subcontractor.vicinity || "Address not available"}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Clock className="w-4 h-4 mr-1" />
          {subcontractor.opening_hours?.open_now ? "Open now" : "Closed"}
        </div>

        <Button onClick={toggleDetails} variant="outline" className="w-full mt-4 flex items-center justify-center">
          {showDetails ? (
            <>
              Hide Details
              <ChevronUp className="ml-2 h-4 w-4" />
            </>
          ) : (
            <>
              Show Details
              <ChevronDown className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>

        {showDetails && (
          <div className="mt-4 space-y-4 bg-gray-50 p-4 rounded-md">
            {detailsLoading && <p className="text-center">Loading details...</p>}
            {detailsError && <p className="text-red-600 text-center">Error loading details: {detailsError.message}</p>}
            {details && details.result && (
              <div className="space-y-4">
                <div className="flex flex-col space-y-2">
                  {details.result.website && (
                    <a
                      href={details.result.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-blue-600 hover:underline"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Visit Website
                    </a>
                  )}
                  {details.result.international_phone_number && (
                    <a
                      href={`tel:${details.result.international_phone_number}`}
                      className="flex items-center text-green-600 hover:underline"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      {details.result.international_phone_number}
                    </a>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">Recent Reviews</h4>
                  {details.result.reviews?.slice(0, 3).map((review, index) => (
                    <div key={index} className="bg-white p-3 rounded-md shadow-sm mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{review.author_name}</span>
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                          {review.rating} ★
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{review.text}</p>
                      <p className="text-xs text-gray-400 mt-1">{review.relative_time_description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default Subcontractors

